import { Link } from 'react-router-dom';
import type { ProcessedApiKey } from '../types';
import { useLanguage } from '../i18n';
import styles from './ApiKeysManager.module.css';

interface ApiKeysManagerProps {
  apiKeys: ProcessedApiKey[];
  onDelete: (id: string) => void;
}

export function ApiKeysManager({ apiKeys, onDelete }: ApiKeysManagerProps) {
  const { t, language } = useLanguage();

  const getStatusColor = (apiKey: ProcessedApiKey) => {
    if (!apiKey.isActive) return 'expired';
    if (apiKey.daysRemaining <= 2) return 'critical';
    if (apiKey.daysRemaining <= 6) return 'warning';
    return 'healthy';
  };

  const handleDelete = (id: string) => {
    if (confirm(t.apiKeys.deleteConfirm)) {
      onDelete(id);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t.apiKeys.title}</h2>
        <Link to="/add-key" className={styles.addBtn}>
          {t.apiKeys.addKey}
        </Link>
      </div>

      {apiKeys.length === 0 ? (
        <div className={styles.emptyState}>
          <p>{t.apiKeys.noKeys}</p>
          <p className={styles.emptyHint}>{t.apiKeys.addFirstKey}</p>
        </div>
      ) : (
        <div className={styles.keysList}>
          {apiKeys.map(apiKey => {
            const status = getStatusColor(apiKey);
            return (
              <div key={apiKey.id} className={`${styles.keyCard} ${styles[status]}`}>
                <div className={styles.keyHeader}>
                  <div className={styles.keyInfo}>
                    <span className={styles.keyName}>{apiKey.platformDisplayName}</span>
                    <span className={styles.keyValue}>{apiKey.keyType}</span>
                  </div>
                  <span className={`${styles.badge} ${styles[status]}`}>
                    {apiKey.isActive
                      ? apiKey.expiresAt
                        ? `${apiKey.daysRemaining} ${t.apiKeys.daysLeft}`
                        : t.apiKeys.active
                      : t.apiKeys.expired}
                  </span>
                </div>

                <div className={styles.keyMeta}>
                  <span className={styles.metaItem}>
                    {t.apiKeys.expires} {formatDate(apiKey.expiresAt)}
                  </span>
                  {apiKey.sellerId && (
                    <span className={styles.metaItem}>
                      {t.apiKeys.sellerId}: {apiKey.sellerId}
                    </span>
                  )}
                </div>

                {apiKey.expiresAt && (
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${Math.max(0, Math.min(100, (apiKey.daysRemaining / 31) * 100))}%` }}
                    />
                  </div>
                )}

                <div className={styles.keyActions}>
                  <button
                    onClick={() => handleDelete(apiKey.id)}
                    className={styles.deleteBtn}
                  >
                    {t.delete}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
